import { useTranslation } from 'react-i18next';
import { AlertCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatShare } from './usage-format';

/** The small pieces every usage panel is assembled from. */

export function ErrorNotice({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <div className="space-y-2">
        <p>{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Saves the table above it as a CSV.
 *
 * Under each table rather than one button for the page: the tables have
 * different shapes, and a single file holding all of them is a file nobody can
 * sort.
 */
export function ExportButton({ onExport }: { onExport: () => void }) {
  const { t } = useTranslation();
  return (
    <Button variant="ghost" size="sm" onClick={onExport}>
      <Download />
      {t('usage.exportCsv')}
    </Button>
  );
}

export function Empty({ children }: { children: string }) {
  return (
    <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
      {children}
    </p>
  );
}

/**
 * A share with a bar under it, for a column of them.
 *
 * The number is the reading and wears ordinary ink; the bar is only there to
 * let the eye run down a column, and it is the bar that carries the subject's
 * colour. `scale` is the largest share in the whole table, so a bar's length
 * means the same thing in every column.
 */
export function ShareCell({
  share,
  scale,
  color,
}: {
  share: number;
  scale: number;
  color: string;
}) {
  return (
    <div className="space-y-1">
      <span className="text-sm tabular-nums">{formatShare(share)}</span>
      <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
        <div
          className="h-full rounded-full"
          style={{
            width: `${scale > 0 ? Math.min(100, (share / scale) * 100) : 0}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}
